import React from "react";
import Markdown from "markdown-to-jsx";
import styled from "styled-components/macro";
import { ExportableTable } from "./ExportableTable";
import { ResultTable } from "./ResultTable";
import { isSafeExportHref } from "./tableExport";
import type { ChatMessage } from "./types";

const MarkdownBody = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  line-height: 1.6;
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    line-height: 1.4;
    margin: 1.5em 0 0.6em;
    font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  }
  & h1 {
    font-size: ${({ theme }) => theme.resolve("font_size_base")};
  }
  & h2 {
    font-size: ${({ theme }) => theme.resolve("font_size_base")};
  }
  & h3,
  & h4,
  & h5,
  & h6 {
    font-size: ${({ theme }) => theme.resolve("font_size_md")};
  }
  & p {
    margin: 1em 0;
  }
  & ul,
  & ol {
    margin: 0.75em 0;
    padding-inline-start: 1.75em;
    list-style-position: outside;
  }
  & ol {
    padding-inline-start: 2em;
  }
  & li {
    padding-inline-start: 0.2em;
  }
  & li + li {
    margin-top: 0.4em;
  }
  & li > p {
    margin: 0.5em 0;
  }
  & li > :first-child {
    margin-top: 0;
  }
  & li > :last-child {
    margin-bottom: 0;
  }
  & li > ul,
  & li > ol {
    margin-top: 0.35em;
    margin-bottom: 0.35em;
  }
  & li:has(> p) + li {
    margin-top: 0.65em;
  }
  & a {
    color: ${({ theme }) => theme.brandColor};
    text-decoration: underline;
  }
  & a:focus-visible {
    outline: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.brandColor};
    outline-offset: 2px;
  }
  & blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    background: #f6f7f9;
    border-radius: ${({ theme }) => theme.resolve("radius_sm")};
    border-left: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.primaryBorder};
    color: ${({ theme }) => theme.secondaryTextColor};
  }
  & code {
    font-family: monospace;
    font-size: 0.9em;
    background: #f6f7f9;
    padding: 0.15em 0.3em;
    border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  }
  & pre {
    overflow-x: auto;
    padding: 1em;
    background: #f6f7f9;
    border-radius: ${({ theme }) => theme.resolve("radius_md")};
  }
  & pre code {
    padding: 0;
    overflow-wrap: normal;
  }
  & hr {
    border: 0;
    border-top: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.primaryBorder};
    margin: 1em 0;
  }
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
`;

function MarkdownAnchor({
  href,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (!isSafeExportHref(href)) return <span {...rest}>{children}</span>;
  return /^https?:\/\//i.test(href as string) ? (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  ) : (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

export function MessageContent({
  message,
  tenantId,
  chatBotName,
}: {
  message: ChatMessage;
  tenantId?: string;
  chatBotName?: string;
}) {
  const tables = message.tables || [];
  const placed = new Set<string>();
  const renderTable = (table: typeof tables[number]) => (
    <ResultTable
      key={table.id}
      table={table}
      tenantId={tenantId}
      messageId={message.server_message_id}
      chatBotName={chatBotName}
    />
  );
  // The server includes a Markdown preview for older open clients. Replace
  // only explicitly bound blocks; unrelated Markdown tables remain local.
  const content = (message.content || "").replace(
    /<!-- agentic-result-table:([\w-]+) -->[\s\S]*?<!-- \/agentic-result-table:\1 -->/g,
    (fallback, id: string) =>
      tables.some((table) => table.id === id) ? `[[table:${id}]]` : fallback
  );
  const parts = content.split(/(\[\[table:[^\]\n]+\]\])/g);
  return (
    <MarkdownBody>
      {parts.map((part, index) => {
        const token = /^\[\[table:([^\]\n]+)\]\]$/.exec(part);
        if (token) {
          const table = tables.find((item) => item.id === token[1]);
          if (!table || placed.has(table.id)) return null;
          placed.add(table.id);
          return renderTable(table);
        }
        if (!part.trim()) return null;
        return (
          <Markdown
            key={`text-${index}`}
            options={{
              overrides: {
                a: { component: MarkdownAnchor },
                table: {
                  component: ExportableTable,
                  props: { chatBotName },
                },
              },
            }}
          >
            {part}
          </Markdown>
        );
      })}
      {tables.filter((table) => !placed.has(table.id)).map(renderTable)}
    </MarkdownBody>
  );
}
